import { CSSProperties, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import styles from './GlassBox.module.scss';

type GlassBoxProps = {
  children: ReactNode;
  gradient: string;
  showBorder?: boolean;
  borderWidth?: string;
  className?: string;
  contentClassName?: string;
  borderRadius?: string;
  backgroundOpacity?: number;
  blurAmount?: string;
  shadowVariant?: 'blue' | 'orange' | 'mint' | 'green' | 'grey' | 'red';
  overflowVisible?: boolean | 'red';
  disableHover?: boolean;
};

export const GlassBox = ({
  children,
  gradient,
  showBorder = true,
  borderWidth = '1px',
  className = '',
  contentClassName = '',
  borderRadius = '10px',
  backgroundOpacity = 0.2,
  blurAmount = '20px',
  shadowVariant,
  overflowVisible = false,
  disableHover = false,
}: GlassBoxProps) => {
  const containerStyle: CSSProperties = {
    background: `rgba(16, 16, 16, ${backgroundOpacity})`,
    backdropFilter: `blur(${blurAmount})`,
    WebkitBackdropFilter: `blur(${blurAmount})`,
    backgroundClip: 'padding-box',
    borderRadius,
    overflow: overflowVisible ? 'visible' : 'hidden',
  };

  const borderStyle: CSSProperties = {
    background: gradient,
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    maskComposite: 'exclude',
    padding: borderWidth,
    borderRadius,
  };

  const variantClassMap: Record<string, string> = {
    blue: styles.variantBlue,
    orange: styles.variantOrange,
    mint: styles.variantMint,
    green: styles.variantGreen,
    grey: styles.variantGrey,
    red: styles.variantRed,
  };

  const variantClass = shadowVariant ? variantClassMap[shadowVariant] : '';
  const hoverDisabledClass = disableHover ? styles.glassBoxNoHover : '';

  return (
    <div className={twMerge('tw-relative', styles.glassBox, variantClass, hoverDisabledClass, className)} style={containerStyle}>
      {showBorder && (
        <div
          className={twMerge('tw-pointer-events-none tw-absolute tw-inset-0', contentClassName)}
          style={borderStyle}
        />
      )}
      {children}
    </div>
  );
};
